import { type FC, type ReactNode, useCallback, useState } from "react";

import { Button, Dropdown, PlacementTypes } from "@snapchat/snap-design-system";
import { EllipsisIcon } from "@snapchat/snap-design-system-icons";

import { type LensCreatorPayoutEligibility } from "../../../../gql-generated/types";
import { getKeys } from "../../../../shared/utils/object";
import { downloadLensScanCode } from "../../../effects";
import { type LensActionsVisibilityState } from "../../../state";
import { type ScanCode } from "../../../utils/getLensScanCode";
import { useScanCodeMessages } from "../../lens-source/lens-scan-code/useScanCodeMessages";
import { DeleteLensButton } from "../delete-lens";

import { MeatBallsAction, MeatBallsMenuContainer, MeatBallsMenuError } from "./MeatBallsMenu.styles";

interface MeatBallsMenu {
    lensId: string;
    lensCreatorPayoutEligibility: LensCreatorPayoutEligibility;
    scanCode?: ScanCode;
    actionsConfig: LensActionsVisibilityState;
    children?: ReactNode;
    onVisibleChange?: (visible: boolean) => void;
    scanCodeLoading?: boolean;
    scanCodeError?: boolean;
    onRetryScanCode?: () => void;
}

export const MeatBallsMenu: FC<MeatBallsMenu> = ({
    lensId,
    lensCreatorPayoutEligibility,
    scanCode,
    actionsConfig,
    children,
    onVisibleChange,
    scanCodeLoading = false,
    scanCodeError = false,
    onRetryScanCode,
}) => {
    const scanCodeMessages = useScanCodeMessages();
    const [downloadFailed, setDownloadFailed] = useState(false);

    const downloadScanCodeAction = useCallback(() => {
        if (!scanCode) {
            return;
        }
        setDownloadFailed(false);
        downloadLensScanCode(scanCode).catch(() => {
            setDownloadFailed(true);
        });
    }, [scanCode]);

    // The menu stays mounted, so clear the error on open and close: a download can still
    // reject after the menu was closed, and the next open must start clean.
    const handleVisibleChange = useCallback(
        (visible: boolean) => {
            setDownloadFailed(false);
            onVisibleChange?.(visible);
        },
        [onVisibleChange],
    );

    if (getKeys(actionsConfig).length === 0 || Object.values(actionsConfig).every((val) => !val)) {
        return (
            <Button shape="circle" isDisabled={true}>
                <EllipsisIcon />
            </Button>
        );
    }

    return (
        <Dropdown
            placement={PlacementTypes.BOTTOM_RIGHT}
            onVisibleChange={handleVisibleChange}
            overlay={
                <MeatBallsMenuContainer>
                    {actionsConfig.downloadQrCode && scanCodeLoading && (
                        <Button isDisabled={true}>{scanCodeMessages.download}</Button>
                    )}
                    {actionsConfig.downloadQrCode && !scanCodeLoading && scanCodeError && (
                        <>
                            <MeatBallsMenuError role="alert">{scanCodeMessages.loadError}</MeatBallsMenuError>
                            <Button onClick={onRetryScanCode}>{scanCodeMessages.retry}</Button>
                        </>
                    )}
                    {actionsConfig.downloadQrCode && !scanCodeLoading && !scanCodeError && scanCode && (
                        <>
                            <MeatBallsAction onClick={downloadScanCodeAction}>
                                {scanCodeMessages.download}
                            </MeatBallsAction>
                            {downloadFailed && (
                                <MeatBallsMenuError role="alert">{scanCodeMessages.downloadError}</MeatBallsMenuError>
                            )}
                        </>
                    )}
                    {actionsConfig.deleteLens && (
                        <DeleteLensButton
                            lensId={lensId}
                            lensCreatorPayoutEligibility={lensCreatorPayoutEligibility}
                            isMeatballMenu={true}
                        >
                            <MeatBallsAction>Delete</MeatBallsAction>
                        </DeleteLensButton>
                    )}
                </MeatBallsMenuContainer>
            }
        >
            {children}
        </Dropdown>
    );
};
